import {
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { useEffect, useState, useContext } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useSnackbar } from "../../contexts/InfoContext";
import editPrices from "../../api/services/prices/setPrices";
import { SpinnerContext } from "../../contexts/SpinnerContext";
import useUserClaims from "../../hooks/useUserClaims";
import getBranchPrice from "../../api/services/prices/get_branch_prices";
import createDefaultPrices from "../../api/services/prices/create_default_prices";
import getRequiredUserData from "../../utils/getBranchInfo";
import getBranchCompanyGain from "../../api/services/prices/get_branch_company_gain";

const defaultData = {
  fixedSalary: "",
  asbezaPrice: "",
  card_collect_price: "",
  card_fee_price: "",
  card_distribute_price: "",
  water_collect_price: "",
  wifi_collect_price: "",
  water_distribute_price: "",
  wifi_distribute_price: "",
  percentage: "",
  percentageSet: false,
};

const DeliveryGainGrid = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const { openSnackbar } = useSnackbar();
  const { setIsSubmitting } = useContext(SpinnerContext);
  const userClaims = useUserClaims(user);
  const branchData = getRequiredUserData();
  const [customerType, setCustomerType] = useState("regular");
  const [companyData, setCompanyData] = useState({});
  const [data, setData] = useState({ ...defaultData });
  const [manualPrices, setManualPrices] = useState({
    asbezaPrice: "",
    card_distribute_price: "",
    card_fee_price: "",
    water_distribute_price: "",
    wifi_distribute_price: "",
  });
  const [editMode, setEditMode] = useState({
    fixedSalary: false,
    asbezaPrice: false,
    card_collect_price: false,
    card_fee_price: false,
    card_distribute_price: false,
    water_collect_price: false,
    wifi_collect_price: false,
    water_distribute_price: false,
    wifi_distribute_price: false,
    percentage: false,
  });

  useEffect(() => {
    const fetchPrices = async () => {
      const branchId = branchData?.requiredId;
      if (!branchId) return;

      try {
        const prices = await getBranchPrice(branchId, customerType);

        // Check if we got an empty object (no document exists with values)
        const hasValues =
          Object.keys(prices).length > 0 &&
          Object.values(prices).some(
            (value) => value !== null && value !== undefined && value !== ""
          );

        if (!hasValues) {
          console.log(
            `No ${customerType} customer prices found. Creating default document.`
          );

          try {
            await createDefaultPrices(user, branchId, customerType);
            console.log(
              `Default ${customerType} customer document created successfully.`
            );
          } catch (createError) {
            console.error(
              `Error creating default ${customerType} document:`,
              createError
            );
          }

          // Use default values in the form
          setData({ ...defaultData });
          setManualPrices({
            asbezaPrice: "",
            card_distribute_price: "",
            card_fee_price: "",
            water_distribute_price: "",
            wifi_distribute_price: "",
          });
        } else {
          // Reset data to default values first, then apply fetched prices
          setData({
            ...defaultData,
            ...prices,
          });
          setManualPrices({
            asbezaPrice: prices.asbezaPrice || "",
            card_distribute_price: prices.card_distribute_price || "",
            card_fee_price: prices.card_fee_price || "",
            water_distribute_price: prices.water_distribute_price || "",
            wifi_distribute_price: prices.wifi_distribute_price || "",
          });
        }
      } catch (error) {
        console.error(`Error fetching ${customerType} branch prices:`, error);
        // If there's an error fetching prices, try to create a default document
        console.log(
          `Error fetching prices for ${customerType}. Attempting to create default document.`
        );

        try {
          await createDefaultPrices(user, branchId, customerType);
          console.log(
            `Default ${customerType} customer document created after error.`
          );
        } catch (createError) {
          console.error(
            `Error creating default ${customerType} document after fetch error:`,
            createError
          );
        }

        // Use default values in the form
        setData({ ...defaultData });
        setManualPrices({
          asbezaPrice: "",
          card_distribute_price: "",
          card_fee_price: "",
          water_distribute_price: "",
          wifi_distribute_price: "",
        });
      }
    };

    fetchPrices();
  }, [branchData?.requiredId, customerType]);

  useEffect(() => {
    if (branchData?.requiredId) {
      getBranchCompanyGain(branchData?.requiredId)
        .then((data) => {
          setCompanyData(data);
        })
        .catch((error) => {
          console.error("Error fetching branch company gain:", error);
        });
    }
  }, [branchData?.requiredId]);

  useEffect(() => {
    if (data.percentageSet && data.percentage) {
      const percentage = parseFloat(data.percentage);
      if (isNaN(percentage) || percentage <= 0) return;

      const calculatedPrices = {};
      if (manualPrices.asbezaPrice != null && manualPrices.asbezaPrice > 0) {
        const calculatedValue = Math.round(
          manualPrices.asbezaPrice * (percentage / 100)
        );
        console.log("Percentage calculation:", {
          asbeza_profit: manualPrices.asbezaPrice,
          percentage: percentage,
          calculatedValue: calculatedValue,
        });
        calculatedPrices.asbezaPrice = calculatedValue.toString();
      }

      setData((prevData) => ({
        ...prevData,
        ...calculatedPrices,
      }));
    } else if (!data.percentageSet) {
      // Only update asbezaPrice when percentage is disabled and we have a valid manual price
      if (manualPrices.asbezaPrice != null && manualPrices.asbezaPrice !== "") {
        setData((prevData) => ({
          ...prevData,
          asbezaPrice: manualPrices.asbezaPrice.toString(),
        }));
      }
    }
  }, [data.percentageSet, data.percentage, manualPrices.asbezaPrice]);

  const showData = {
    fixedSalary: data?.fixedSalary || "",
    card_collect_price: data?.card_collect_price || "",
    card_fee_price: data?.card_fee_price || "",
    card_distribute_price: data?.card_distribute_price || "",
    water_collect_price: data?.water_collect_price || "",
    wifi_collect_price: data?.wifi_collect_price || "",
    water_distribute_price: data?.water_distribute_price || "",
    asbezaPrice: data?.asbezaPrice || "",
    wifi_distribute_price: data?.wifi_distribute_price || "",
    percentage: data?.percentage || "",
  };

  const names = {
    fixedSalary: "Fixed Salary",
    card_collect_price: "Card Collect Price",
    card_fee_price: "Card Fee Price",
    card_distribute_price: "Card Distribute Price",
    water_collect_price: "Water Collect Price",
    wifi_collect_price: "WiFi Collect Price",
    water_distribute_price: "Water Distribute Price",
    asbezaPrice: "Asbeza Price",
    wifi_distribute_price: "WiFi Distribute Price",
    percentage: "Percentage",
  };

  const handleInputChange = (key, value) => {
    setData((prevData) => ({
      ...prevData,
      [key]: value,
    }));
    if (
      [
        "asbezaPrice",
        "card_distribute_price",
        "card_fee_price",
        "water_distribute_price",
        "wifi_distribute_price",
      ].includes(key)
    ) {
      setManualPrices((prev) => ({
        ...prev,
        [key]: value,
      }));
    }
  };

  const handleEditButtonClick = (key) => {
    setEditMode((prevEditMode) => ({
      ...prevEditMode,
      [key]: true,
    }));
  };

  const handleSaveButtonClick = async (key) => {
    if (data[key] === "") {
      openSnackbar(`${names[key]} cannot be empty!`, "error");
      setIsSubmitting(false);
      return;
    }

    setIsSubmitting(true);
    setEditMode((prevEditMode) => ({
      ...prevEditMode,
      [key]: false,
    }));

    try {
      const updateData = { [key]: parseFloat(data[key]) };
      if (key === "percentage") {
        updateData.percentageSet = data.percentageSet;
      }

      if (key === "asbezaPrice" && data.percentageSet) {
        // Validate the manual price before saving
        const manualPrice = parseFloat(data[key]);
        if (isNaN(manualPrice) || manualPrice <= 0) {
          openSnackbar(
            "Manual asbeza price must be a positive number!",
            "error"
          );
          setIsSubmitting(false);
          return;
        }

        setManualPrices((prev) => ({
          ...prev,
          asbezaPrice: manualPrice,
        }));
        openSnackbar(
          `Manual asbeza price saved for when percentage mode is disabled!`,
          "success"
        );
      } else {
        await editPrices(
          user,
          updateData,
          branchData?.requiredId,
          customerType
        );

        if (
          key === "percentage" &&
          data.percentageSet &&
          manualPrices.asbezaPrice != null &&
          manualPrices.asbezaPrice > 0
        ) {
          const percentage = parseFloat(data[key]);
          if (!isNaN(percentage) && percentage > 0) {
            const calculatedAsbezaPrice = Math.round(
              manualPrices.asbezaPrice * (percentage / 100)
            ).toString();

            setData((prevData) => ({
              ...prevData,
              asbezaPrice: calculatedAsbezaPrice,
            }));
          }
        }

        openSnackbar(`${names[key]} updated successfully!`, "success");
      }
    } catch (error) {
      openSnackbar(
        error.response?.data?.message || "An unexpected error occurred.",
        error.response?.data?.type || "error"
      );
    }
    setIsSubmitting(false);
  };

  const handlePercentageToggle = async () => {
    setIsSubmitting(true);
    const newPercentageSet = !data.percentageSet;

    try {
      // Validation: Check if we have valid data for the toggle
      if (
        !newPercentageSet &&
        (!manualPrices.asbezaPrice || manualPrices.asbezaPrice <= 0)
      ) {
        openSnackbar(
          "Cannot disable percentage mode: No valid manual asbeza price found. Please set a manual price first.",
          "error"
        );
        setIsSubmitting(false);
        return;
      }

      if (
        newPercentageSet &&
        (!data.percentage || parseFloat(data.percentage) <= 0)
      ) {
        openSnackbar(
          "Cannot enable percentage mode: Please set a valid percentage first.",
          "error"
        );
        setIsSubmitting(false);
        return;
      }

      const updateData = {
        percentageSet: newPercentageSet,
        percentage: parseFloat(data.percentage) || 0,
      };

      if (!newPercentageSet) {
        updateData.asbezaPrice = parseFloat(manualPrices.asbezaPrice) || 0;
      }

      await editPrices(user, updateData, branchData?.requiredId, customerType);

      let newAsbezaPrice = data.asbezaPrice;

      if (newPercentageSet) {
        if (
          manualPrices.asbezaPrice != null &&
          manualPrices.asbezaPrice > 0 &&
          data.percentage
        ) {
          const percentage = parseFloat(data.percentage);
          if (!isNaN(percentage) && percentage > 0) {
            newAsbezaPrice = Math.round(
              manualPrices.asbezaPrice * (percentage / 100)
            ).toString();
          }
        }
      } else {
        // Use manual price if available and valid
        if (manualPrices.asbezaPrice != null && manualPrices.asbezaPrice > 0) {
          newAsbezaPrice = manualPrices.asbezaPrice.toString();
        } else {
          newAsbezaPrice = "";
        }
      }

      setData((prevData) => ({
        ...prevData,
        percentageSet: newPercentageSet,
        asbezaPrice: newAsbezaPrice,
      }));

      openSnackbar(
        `Percentage toggle ${
          newPercentageSet ? "enabled" : "disabled"
        } successfully!`,
        "success"
      );
    } catch (error) {
      openSnackbar(
        error.response?.data?.message || "An unexpected error occurred.",
        error.response?.data?.type || "error"
      );
    }
    setIsSubmitting(false);
  };

  const handleCustomerTypeChange = (event, newCustomerType) => {
    if (newCustomerType !== null) {
      // Reset form data when switching customer types
      setData({ ...defaultData });
      setManualPrices({
        asbezaPrice: "",
        card_distribute_price: "",
        card_fee_price: "",
        water_distribute_price: "",
        wifi_distribute_price: "",
      });
      setEditMode({
        fixedSalary: false,
        asbezaPrice: false,
        card_collect_price: false,
        card_fee_price: false,
        card_distribute_price: false,
        water_collect_price: false,
        wifi_collect_price: false,
        water_distribute_price: false,
        wifi_distribute_price: false,
        percentage: false,
      });
      setCustomerType(newCustomerType);
    }
  };

  return (
    <Paper
      elevation={5}
      style={{
        padding: "20px",
        margin: "20px",
        backgroundColor: theme.palette.background.alt,
        color: theme.palette.secondary[300],
      }}
    >
      <Typography
        variant="h4"
        align="center"
        style={{ fontWeight: "bold", marginBottom: "20px" }}
      >
        Service Price Set
      </Typography>
      <ToggleButtonGroup
        value={customerType}
        exclusive
        onChange={handleCustomerTypeChange}
        aria-label="customer type"
        style={{
          marginBottom: "20px",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <ToggleButton value="regular" aria-label="regular customer">
          Regular Customer
        </ToggleButton>
        <ToggleButton value="walking" aria-label="walking customer">
          Walking Customer
        </ToggleButton>
      </ToggleButtonGroup>
      <Grid container spacing={2}>
        {Object.keys(showData).map((key) => (
          <Grid item xs={12} md={6} key={key}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={4} md={4}>
                <Typography variant="subtitle1">{names[key]}</Typography>
              </Grid>
              <Grid item xs={4} md={4}>
                <TextField
                  value={showData[key]}
                  type="number"
                  required={true}
                  onChange={(e) => {
                    const value = e.target.value;
                    const parsedValue = parseFloat(value);
                    // Allow empty string for clearing, but validate positive numbers
                    if (
                      value === "" ||
                      (!isNaN(parsedValue) && parsedValue >= 0)
                    ) {
                      handleInputChange(
                        key,
                        value === "" ? "" : Math.abs(parsedValue)
                      );
                    }
                  }}
                  disabled={
                    !editMode[key] ||
                    !(userClaims.superAdmin || userClaims.admin) ||
                    (data.percentageSet && key === "asbezaPrice")
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={4} md={4}>
                {editMode[key] ? (
                  <Button
                    variant="contained"
                    onClick={() => handleSaveButtonClick(key)}
                  >
                    Save
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={() => handleEditButtonClick(key)}
                    disabled={
                      !(userClaims.superAdmin || userClaims.admin) ||
                      (data.percentageSet && key === "asbezaPrice")
                    }
                  >
                    Edit
                  </Button>
                )}
              </Grid>
            </Grid>
          </Grid>
        ))}
        <Grid
          item
          xs={12}
          style={{ display: "flex", justifyContent: "flex-end" }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={data.percentageSet}
                onChange={handlePercentageToggle}
                disabled={!(userClaims.superAdmin || userClaims.admin)}
              />
            }
            label="Use Percentage"
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default DeliveryGainGrid;
